import { useEffect, useRef, useState } from "react";
import hljs from "highlight.js/lib/core";
import sql from "highlight.js/lib/languages/sql";
import bash from "highlight.js/lib/languages/bash";
import javascript from "highlight.js/lib/languages/javascript";
import json from "highlight.js/lib/languages/json";
import plaintext from "highlight.js/lib/languages/plaintext";
import python from "highlight.js/lib/languages/python";
import typescript from "highlight.js/lib/languages/typescript";
import xml from "highlight.js/lib/languages/xml";
import yaml from "highlight.js/lib/languages/yaml";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Code2,
  Copy,
  Database,
  Download,
  ExternalLink,
  HelpCircle,
  Lightbulb,
  Loader2,
  Network,
  Pencil,
  ShieldX,
  Sparkles,
  Table2,
  User,
  Wrench,
  XCircle,
} from "lucide-react";
import type { ChatTurn, ToolInvocation } from "@/store/chat";
import { ResultsTable } from "./ResultsTable";
import { ActionProposalCard } from "./ActionProposalCard";
import { ChartBlock } from "./ChartBlock";
import { ToolVisualBlock } from "./ToolVisualBlock";
import { downloadCsv, toCsv } from "@/lib/csv";
import { downloadJson, downloadMarkdown, type ExportableTurn } from "@/lib/exportBundle";
import { createSavedQuestion } from "@/lib/api";
import { KpiTiles } from "./KpiTiles";
import { VisualBlock } from "./viz/VisualBlock";
import { validateNarrativeNumbers } from "@/lib/format";

// Languages registered with hljs for fenced-code-block highlighting in
// narrative answers. Aligned with the cognos-plugin's curated set so a
// LIDA answer that emits ```sql / ```json / ```python renders the same
// way in both UIs.
hljs.registerLanguage("sql",        sql);
hljs.registerLanguage("bash",       bash);
hljs.registerLanguage("shell",      bash);
hljs.registerLanguage("sh",         bash);
hljs.registerLanguage("javascript", javascript);
hljs.registerLanguage("js",         javascript);
hljs.registerLanguage("typescript", typescript);
hljs.registerLanguage("ts",         typescript);
hljs.registerLanguage("json",       json);
hljs.registerLanguage("python",     python);
hljs.registerLanguage("py",         python);
hljs.registerLanguage("xml",        xml);
hljs.registerLanguage("html",       xml);
hljs.registerLanguage("yaml",       yaml);
hljs.registerLanguage("yml",        yaml);
hljs.registerLanguage("plaintext",  plaintext);
hljs.registerLanguage("text",       plaintext);

type Props = {
  turn: ChatTurn;
  onFollowUp?: (q: string) => void;
  /**
   * Load the question back into the composer so the user can tweak it and
   * re-send — useful when the phrasing led the LLM down a wrong path. Unlike
   * onFollowUp this must NOT auto-send; it just pre-fills the textarea.
   */
  onEditQuestion?: (q: string) => void;
  /**
   * Feature #1 — refine the chart against its cached rows. Only the most
   * recent assistant turn with a vizSpec should pass this in; older turns
   * stay read-only.
   */
  onChartFollowUp?: (intent: string, columnNames: string[], rows: Array<Array<unknown>>) => void;
  /**
   * Feature #2 — re-run the Cognos report with new prompt-parameter values.
   * Only fires for charts whose Visual carries a sourceRef for
   * `cognos.report.run`.
   */
  onRerunWithFilters?: (
    sourceRef: { kind: string; params: Record<string, unknown> },
    values:    Record<string, string | string[]>,
  ) => void;
  /** Feature #3 — analyse the cached data with a narrative question. */
  onChartAskAboutData?: (intent: string, columnNames: string[], rows: Array<Array<unknown>>) => void;
  /** True while any of the three viz follow-up actions is in flight. */
  chartActionsBusy?: boolean;
};

export function MessageCard({
  turn, onFollowUp, onEditQuestion,
  onChartFollowUp, onRerunWithFilters, onChartAskAboutData, chartActionsBusy,
}: Props) {
  const validation = turn.narrative
    ? validateNarrativeNumbers(turn.narrative.summary, turn.rows?.rows, turn.rows?.rowCount)
    : null;

  return (
    <article className="space-y-4">
      {/* User question bubble */}
      <div className="group/question flex items-start gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-datumcraft-steel text-white shadow-sm">
          <User size={16} aria-hidden />
        </div>
        <div className="relative min-w-0 flex-1 rounded-2xl rounded-tl-sm bg-white px-4 py-2.5 pr-32 shadow-card">
          <p className="text-2xs font-medium uppercase tracking-wide text-datumcraft-slate">You asked</p>
          <p className="mt-0.5 text-[15px] font-medium text-datumcraft-navy">{turn.question}</p>
          <QuestionActions
            question={turn.question}
            turn={turn}
            onEdit={onEditQuestion}
          />
        </div>
      </div>

      {/* Assistant response */}
      <div className="flex items-start gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-datumcraft-accent to-datumcraft-accentDark text-white shadow-sm">
          <Sparkles size={16} aria-hidden />
        </div>
        <div className="min-w-0 flex-1 space-y-3 rounded-2xl rounded-tl-sm border border-datumcraft-slate/20 bg-white p-4 shadow-card">
          <StatusHeader turn={turn} />

          {turn.clarification && (
            <Callout icon={<HelpCircle size={16} />} tone="info" title="Clarification needed">
              {turn.clarification}
            </Callout>
          )}

          {turn.rejected && (
            <Callout icon={<ShieldX size={16} />} tone="danger" title="Request blocked">
              {turn.rejected.violations.join("; ") || "This request was rejected for safety reasons."}
            </Callout>
          )}

          {turn.errorMessage && (
            <Callout icon={<XCircle size={16} />} tone="danger" title="Something went wrong">
              {turn.errorMessage}
            </Callout>
          )}

          {turn.toolCalls && turn.toolCalls.length > 0 && (
            <ToolCallsStrip calls={turn.toolCalls} />
          )}

          {/* When the turn also produced an interactive vizSpec (deck.gl
              lineage scene, etc.) we skip the PNG block — same tool call
              produces both as a fallback pair, and showing both would
              just stack a static image above the interactive scene.
              The PNG is still cached server-side and available via the
              resource_link if the user needs to save it. */}
          {turn.visuals && turn.visuals.length > 0 && !turn.vizSpec && (
            <section className="space-y-2">
              {turn.visuals.map((v, i) => (
                <ToolVisualBlock
                  key={`${v.callId}-${i}`}
                  mimeType={v.mimeType}
                  dataUrl={v.dataUrl}
                  index={i + 1}
                />
              ))}
            </section>
          )}

          {turn.charts && turn.charts.length > 0 && (
            <section className="space-y-2">
              {turn.charts.map((c, i) => (
                <ChartBlock key={`${c.callId}-${i}`} spec={c.spec} />
              ))}
            </section>
          )}

          {turn.vizSpec && (
            <VisualBlock
              vizSpec={turn.vizSpec}
              sourceRef={turn.sourceRef}
              busy={chartActionsBusy ?? false}
              onFollowUp={onChartFollowUp}
              onAskAboutData={onChartAskAboutData}
              onRerunWithFilters={onRerunWithFilters}
            />
          )}

          {turn.tables && turn.tables.length > 0 && (
            <section className="space-y-3">
              {/*
                Server-emitted canonical tables. Rendered before the
                narrative so the user sees the deterministic truth first
                and the LLM's prose interpretation second. Each table is
                fully self-contained (sort, filter, exact row count, CSV
                export) — column shapes / types come from the tool's
                structured response, not the LLM's transcription.
                Title is derived from the matching toolCall (callId →
                tool name) so stacked tables in one answer can be told
                apart at a glance.
              */}
              {turn.tables.map((t, i) => {
                const sourceTool = turn.toolCalls?.find((c) => c.callId === t.callId)?.tool;
                return (
                  <ResultsTable
                    key={`${t.callId}-${i}`}
                    columns={t.columnNames}
                    rows={t.rows}
                    question={turn.question}
                    title={humanizeToolName(sourceTool)}
                    tool={sourceTool}
                  />
                );
              })}
            </section>
          )}

          {turn.resources && turn.resources.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {turn.resources.map((r, i) => (
                <a
                  key={`${r.callId}-${i}`}
                  href={r.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-full border border-datumcraft-slate/30 bg-white px-3 py-1 text-xs text-datumcraft-ink hover:border-datumcraft-accentDark hover:bg-datumcraft-accent/10 hover:text-datumcraft-navy"
                >
                  <ExternalLink size={12} aria-hidden />
                  <span>{r.label || "Open resource"}</span>
                </a>
              ))}
            </div>
          )}

          {turn.narrative && (
            <section className="prose-narrative space-y-3">
              <NarrativeMarkdown
                text={turn.narrative.summary}
                suppressTables={(turn.tables?.length ?? 0) > 0}
              />

              {turn.narrative.keyFindings?.length > 0 && (
                <ul className="space-y-1.5 text-sm text-datumcraft-ink">
                  {turn.narrative.keyFindings.map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle2
                        size={14}
                        className="mt-0.5 shrink-0 text-datumcraft-accentDark"
                        aria-hidden
                      />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          {turn.rows && turn.rows.rowCount > 0 && (
            <section className="space-y-3">
              <KpiTiles
                columns={turn.rows.columnNames}
                rows={turn.rows.rows}
                rowCount={turn.rows.rowCount}
              />
              <div className="flex items-center gap-2 pt-1 text-2xs font-medium uppercase tracking-wide text-datumcraft-slate">
                <Table2 size={14} className="text-datumcraft-steel" aria-hidden />
                <span>Rows</span>
              </div>
              <ResultsTable
                columns={turn.rows.columnNames}
                rows={turn.rows.rows}
                question={turn.question}
                validated={validation?.ok}
                validationNote={
                  validation && !validation.ok
                    ? `The narrative mentions numbers (${validation.missing.join(", ")}) not found in the returned rows.`
                    : undefined
                }
              />
            </section>
          )}

          {turn.rows && turn.rows.rowCount === 0 && !turn.rejected && !turn.errorMessage && (
            <Callout icon={<Lightbulb size={16} />} tone="muted" title="No matching records">
              The query ran successfully but didn't return any rows.
            </Callout>
          )}

          {turn.sql && <SqlBlock sql={turn.sql} />}

          {turn.narrative?.followUpQuestions?.length ? (
            <div>
              <p className="mb-1.5 text-2xs font-medium uppercase tracking-wide text-datumcraft-slate">
                Follow-up suggestions
              </p>
              <div className="flex flex-wrap gap-2">
                {turn.narrative.followUpQuestions.map((q, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => onFollowUp?.(q)}
                    className="group inline-flex items-center gap-1 rounded-full border border-datumcraft-slate/40 bg-white px-3 py-1 text-xs text-datumcraft-ink transition-colors hover:border-datumcraft-accentDark hover:bg-datumcraft-accent/10 hover:text-datumcraft-navy"
                  >
                    <span>{q}</span>
                    <ChevronRight
                      size={12}
                      className="text-datumcraft-slate transition-transform group-hover:translate-x-0.5 group-hover:text-datumcraft-accentDark"
                      aria-hidden
                    />
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {turn.narrative?.proposedActions?.map((a, i) => (
            <ActionProposalCard key={i} action={a} />
          ))}
        </div>
      </div>
    </article>
  );
}

/**
 * Hover-revealed controls in the top-right of the user question bubble.
 * Copy puts the verbatim question on the clipboard; Edit loads it into the
 * composer so the user can rephrase and re-send without retyping.
 */
function QuestionActions({
  question,
  turn,
  onEdit,
}: {
  question: string;
  turn: ChatTurn;
  onEdit?: (q: string) => void;
}) {
  const [copied, setCopied] = useState(false);
  const [savedTone, setSavedTone] = useState<"idle" | "saving" | "saved" | "error">("idle");

  function copy() {
    navigator.clipboard.writeText(question).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      },
      () => { /* clipboard blocked — ignore */ },
    );
  }

  async function pin() {
    setSavedTone("saving");
    try {
      const trimmed = question.trim();
      const name = trimmed.length > 80 ? trimmed.slice(0, 80) + "…" : trimmed;
      await createSavedQuestion({
        name: name || "Untitled question",
        question: trimmed,
        pinned: true,
      });
      setSavedTone("saved");
      window.setTimeout(() => setSavedTone("idle"), 1500);
    } catch {
      setSavedTone("error");
      window.setTimeout(() => setSavedTone("idle"), 2000);
    }
  }

  function exportTurn(format: "json" | "md") {
    const payload: ExportableTurn = {
      question: turn.question,
      standaloneQuestion: turn.rewritten,
      sql: turn.sql,
      rows: turn.rows,
      narrative: turn.narrative,
      ranAt: new Date().toISOString(),
    };
    const stamp = new Date().toISOString().slice(0, 10);
    const safe = (turn.question || "answer").replace(/[^a-zA-Z0-9._-]+/g, "-").slice(0, 60);
    if (format === "json") {
      downloadJson(`cloudcraftiq-${safe}-${stamp}.json`, payload);
    } else {
      downloadMarkdown(`cloudcraftiq-${safe}-${stamp}.md`, payload);
    }
  }

  const saveLabel =
    savedTone === "saving" ? "Saving…"
    : savedTone === "saved" ? "Saved"
    : savedTone === "error" ? "Error" : "Save";

  return (
    <div className="absolute right-2 top-2 flex items-center gap-1 opacity-0 transition-opacity focus-within:opacity-100 group-hover/question:opacity-100">
      <button
        type="button"
        onClick={copy}
        title={copied ? "Copied" : "Copy question"}
        aria-label={copied ? "Copied" : "Copy question"}
        className="inline-flex items-center gap-1 rounded border border-datumcraft-slate/30 bg-white px-1.5 py-0.5 text-2xs font-medium text-datumcraft-steel hover:border-datumcraft-accentDark hover:text-datumcraft-navy"
      >
        {copied ? <CheckCircle2 size={12} /> : <Copy size={12} />}
        <span>{copied ? "Copied" : "Copy"}</span>
      </button>
      <button
        type="button"
        onClick={pin}
        disabled={savedTone === "saving"}
        title="Pin this question for later — appears on the Saved Questions page"
        aria-label="Save question"
        className="inline-flex items-center gap-1 rounded border border-datumcraft-slate/30 bg-white px-1.5 py-0.5 text-2xs font-medium text-datumcraft-steel hover:border-datumcraft-accentDark hover:text-datumcraft-navy disabled:opacity-60"
      >
        <Sparkles size={12} />
        <span>{saveLabel}</span>
      </button>
      <button
        type="button"
        onClick={() => exportTurn("md")}
        title="Download this answer as a Markdown report"
        className="inline-flex items-center gap-1 rounded border border-datumcraft-slate/30 bg-white px-1.5 py-0.5 text-2xs font-medium text-datumcraft-steel hover:border-datumcraft-accentDark hover:text-datumcraft-navy"
      >
        <Download size={12} />
        <span>MD</span>
      </button>
      <button
        type="button"
        onClick={() => exportTurn("json")}
        title="Download this answer as a JSON bundle"
        className="inline-flex items-center gap-1 rounded border border-datumcraft-slate/30 bg-white px-1.5 py-0.5 text-2xs font-medium text-datumcraft-steel hover:border-datumcraft-accentDark hover:text-datumcraft-navy"
      >
        <Download size={12} />
        <span>JSON</span>
      </button>
      {onEdit && (
        <button
          type="button"
          onClick={() => onEdit(question)}
          title="Edit and resend this question"
          aria-label="Edit and resend this question"
          className="inline-flex items-center gap-1 rounded border border-datumcraft-slate/30 bg-white px-1.5 py-0.5 text-2xs font-medium text-datumcraft-steel hover:border-datumcraft-accentDark hover:text-datumcraft-navy"
        >
          <Pencil size={12} />
          <span>Edit</span>
        </button>
      )}
    </div>
  );
}

function StatusHeader({ turn }: { turn: ChatTurn }) {
  if (turn.status === "streaming") {
    const lastTool = turn.toolCalls?.[turn.toolCalls.length - 1]?.tool;
    const label = lastTool
      ? `Calling ${lastTool}…`
      : turn.sql
        ? "Running query…"
        : turn.rewritten
          ? "Generating SQL…"
          : "Thinking…";
    return (
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs text-datumcraft-slate" role="status">
          <span className="thinking-dots" aria-hidden>
            <span /><span /><span />
          </span>
          <span>{label}</span>
        </div>
        <SourcePill source={turn.source} />
      </div>
    );
  }
  let left: React.ReactNode;
  if (turn.status === "error") {
    left = (
      <div className="inline-flex items-center gap-1 text-xs font-medium text-datumcraft-danger">
        <AlertTriangle size={12} /> Error
      </div>
    );
  } else if (turn.rejected) {
    left = (
      <div className="inline-flex items-center gap-1 text-xs font-medium text-datumcraft-danger">
        <ShieldX size={12} /> Blocked
      </div>
    );
  } else {
    left = (
      <div className="inline-flex items-center gap-1 text-2xs font-medium uppercase tracking-wide text-datumcraft-accentDark">
        <CheckCircle2 size={12} /> Answered
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between gap-2">
      {left}
      <div className="flex items-center gap-1.5">
        <SourcePill source={turn.source} />
        <CopyResponseButton turn={turn} />
      </div>
    </div>
  );
}

/**
 * Copies the assistant's answer to the clipboard. Prefers the narrative
 * summary (the markdown the user actually sees); falls back to a
 * clarification or error message so there's always something useful to copy
 * even when the turn didn't produce a full narrative. Hidden when the turn
 * has nothing copy-worthy yet.
 */
function CopyResponseButton({ turn }: { turn: ChatTurn }) {
  const [copied, setCopied] = useState(false);
  const text =
    turn.narrative?.summary?.trim() ||
    turn.clarification?.trim() ||
    turn.errorMessage?.trim() ||
    "";
  if (!text) return null;

  function copy() {
    navigator.clipboard.writeText(text).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      },
      () => { /* clipboard blocked — ignore */ },
    );
  }

  return (
    <button
      type="button"
      onClick={copy}
      title={copied ? "Copied" : "Copy response"}
      aria-label={copied ? "Copied" : "Copy response"}
      className="inline-flex items-center gap-1 rounded border border-datumcraft-slate/30 bg-white px-1.5 py-0.5 text-2xs font-medium text-datumcraft-steel hover:border-datumcraft-accentDark hover:text-datumcraft-navy"
    >
      {copied ? <CheckCircle2 size={12} /> : <Copy size={12} />}
      <span>{copied ? "Copied" : "Copy"}</span>
    </button>
  );
}

/**
 * Badge telling the user whether this answer came through the agentic MCP
 * loop or the legacy direct-SQL pipeline. Hidden until we have a definitive
 * signal — streaming turns that haven't emitted `sql` or `tool_started`
 * yet don't get a speculative label.
 */
function SourcePill({ source }: { source?: ChatTurn["source"] }) {
  if (!source || source === "unknown") return null;
  if (source === "mcp") {
    return (
      <span
        title="Answer produced via the agentic loop — the LLM chose tools from the MCP server"
        className="inline-flex items-center gap-1 rounded-full border border-datumcraft-accentDark/30 bg-datumcraft-accent/10 px-2 py-0.5 text-[10px] font-medium text-datumcraft-accentDark"
      >
        <Network size={10} aria-hidden /> via MCP
      </span>
    );
  }
  return (
    <span
      title="Answer produced via the direct-SQL pipeline (natural language → SQL → narrative)"
      className="inline-flex items-center gap-1 rounded-full border border-datumcraft-slate/30 bg-datumcraft-mist px-2 py-0.5 text-[10px] font-medium text-datumcraft-steel"
    >
      <Database size={10} aria-hidden /> via SQL
    </span>
  );
}

function NarrativeMarkdown({
  text,
  suppressTables = false,
}: {
  text: string;
  /**
   * When true, drop markdown tables from the rendered output. Used
   * when the gateway has already emitted canonical {@code tool_table}
   * data for this turn — rendering both produces visible duplication
   * (one styled+sortable, one plain) confusing the user. The
   * canonical table is the source of truth; the markdown one is the
   * fallback only when the gateway can't extract structured rows.
   */
  suppressTables?: boolean;
}) {
  return (
    <div className="markdown-body text-datumcraft-navy">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => <p className="text-[15px] leading-relaxed">{children}</p>,
          h1: ({ children }) => (
            <h3 className="mt-3 text-base font-semibold text-datumcraft-navy">{children}</h3>
          ),
          h2: ({ children }) => (
            <h3 className="mt-3 text-base font-semibold text-datumcraft-navy">{children}</h3>
          ),
          h3: ({ children }) => (
            <h4 className="mt-2 text-sm font-semibold text-datumcraft-navy">{children}</h4>
          ),
          ul: ({ children }) => (
            <ul className="list-disc space-y-1 pl-5 text-datumcraft-ink">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal space-y-1 pl-5 text-datumcraft-ink">{children}</ol>
          ),
          li: ({ children }) => <li className="text-[14px] leading-relaxed">{children}</li>,
          strong: ({ children }) => (
            <strong className="font-semibold text-datumcraft-navy">{children}</strong>
          ),
          em: ({ children }) => <em className="italic">{children}</em>,
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-datumcraft-accentDark underline hover:text-datumcraft-navy"
            >
              {children}
            </a>
          ),
          code: ({ className, children }) => {
            const langMatch = /language-([a-z0-9_+\-]+)/i.exec(className ?? "");
            const lang = langMatch ? langMatch[1].toLowerCase() : "";
            const isBlock = lang.length > 0;
            if (isBlock) {
              return <FencedCodeBlock language={lang}>{String(children ?? "")}</FencedCodeBlock>;
            }
            return (
              <code className="rounded bg-datumcraft-fog px-1 py-0.5 text-[12px] text-datumcraft-ink">
                {children}
              </code>
            );
          },
          // When the turn already has a canonical tool_table rendered,
          // suppress the LLM's markdown table to avoid showing two
          // copies of the same data (one styled+sortable, one plain).
          // The wrapper renderers all return null in suppress mode so
          // the entire table tree drops out of the DOM.
          table: ({ children }) =>
            suppressTables ? null : <ExportableMarkdownTable>{children}</ExportableMarkdownTable>,
          thead: ({ children }) => (suppressTables ? null : <thead>{children}</thead>),
          tbody: ({ children }) => (suppressTables ? null : <tbody>{children}</tbody>),
          th: ({ children }) =>
            suppressTables ? null : (
              <th className="sticky top-0 border-b border-datumcraft-slate/30 bg-datumcraft-fog/95 px-3 py-2 text-left text-[12px] font-semibold uppercase tracking-wide text-datumcraft-navy backdrop-blur">
                {children}
              </th>
            ),
          td: ({ children }) =>
            suppressTables ? null : (
              <td className="border-b border-datumcraft-slate/15 px-3 py-1.5 align-top text-[13px] text-datumcraft-ink">
                {children}
              </td>
            ),
          tr: ({ children }) =>
            suppressTables ? null : (
              <tr className="odd:bg-white even:bg-datumcraft-fog/40 hover:bg-datumcraft-mist/60">{children}</tr>
            ),
          blockquote: ({ children }) => (
            <blockquote className="border-l-2 border-datumcraft-slate/40 pl-3 italic text-datumcraft-ink">
              {children}
            </blockquote>
          ),
          hr: () => <hr className="my-3 border-datumcraft-slate/20" />,
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}

function ToolCallsStrip({ calls }: { calls: NonNullable<ChatTurn["toolCalls"]> }) {
  const count = calls.length;
  const errorCount = calls.filter((c) => c.status === "error").length;
  const pendingCount = calls.filter((c) => c.status === "pending").length;
  // Auto-expand when any tool failed so the user immediately sees which one
  // and why. Otherwise default collapsed to keep the chat surface calm.
  const [open, setOpen] = useState(errorCount > 0);
  return (
    <div className="rounded-lg border border-datumcraft-slate/20 bg-datumcraft-fog/60">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between px-3 py-1.5 text-xs text-datumcraft-steel hover:text-datumcraft-navy"
        aria-expanded={open}
      >
        <span className="inline-flex items-center gap-1.5">
          <Wrench size={12} />
          <span>
            {count === 1 ? "1 tool called" : `${count} tools called`}
          </span>
          {errorCount > 0 && (
            <span className="ml-1 rounded-full bg-datumcraft-danger/10 px-1.5 py-px text-[10px] font-semibold text-datumcraft-danger">
              {errorCount} failed
            </span>
          )}
          {pendingCount > 0 && errorCount === 0 && (
            <span className="ml-1 rounded-full bg-datumcraft-slate/15 px-1.5 py-px text-[10px] font-semibold text-datumcraft-steel">
              {pendingCount} running
            </span>
          )}
        </span>
        <ChevronDown
          size={12}
          className={`transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden
        />
      </button>
      {open && (
        <ul className="space-y-1.5 border-t border-datumcraft-slate/20 px-3 py-2 text-xs">
          {calls.map((c, i) => (
            <ToolCallRow key={c.callId || i} call={c} />
          ))}
        </ul>
      )}
    </div>
  );
}

function ToolCallRow({ call }: { call: ToolInvocation }) {
  return (
    <li className="flex items-start gap-2 text-datumcraft-ink">
      <ToolStatusIcon status={call.status} />
      <div className="min-w-0 flex-1">
        <div className="font-medium text-datumcraft-navy">{call.tool}</div>
        {call.argumentsJson && call.argumentsJson !== "{}" && (
          <code className="block truncate text-[11px] text-datumcraft-slate">
            {call.argumentsJson}
          </code>
        )}
        {call.status === "error" && call.errorMessage && (
          <p className="mt-0.5 text-[11px] text-datumcraft-danger">
            {call.errorMessage}
          </p>
        )}
      </div>
    </li>
  );
}

function ToolStatusIcon({ status }: { status: ToolInvocation["status"] }) {
  if (status === "ok") {
    return (
      <CheckCircle2
        size={12}
        className="mt-0.5 shrink-0 text-datumcraft-accentDark"
        aria-label="succeeded"
      />
    );
  }
  if (status === "error") {
    return (
      <XCircle
        size={12}
        className="mt-0.5 shrink-0 text-datumcraft-danger"
        aria-label="failed"
      />
    );
  }
  return (
    <Loader2
      size={12}
      className="mt-0.5 shrink-0 animate-spin text-datumcraft-steel"
      aria-label="running"
    />
  );
}

type Tone = "info" | "danger" | "muted";

function Callout({
  icon,
  tone,
  title,
  children,
}: {
  icon: React.ReactNode;
  tone: Tone;
  title: string;
  children: React.ReactNode;
}) {
  const toneClass =
    tone === "danger"
      ? "border-datumcraft-danger/30 bg-datumcraft-danger/5 text-datumcraft-danger"
      : tone === "info"
        ? "border-datumcraft-accentDark/30 bg-datumcraft-accent/10 text-datumcraft-navy"
        : "border-datumcraft-slate/30 bg-datumcraft-mist text-datumcraft-ink";
  const iconClass =
    tone === "danger" ? "text-datumcraft-danger" : tone === "info" ? "text-datumcraft-accentDark" : "text-datumcraft-slate";
  return (
    <div role={tone === "danger" ? "alert" : undefined} className={`flex gap-2.5 rounded-lg border p-3 text-sm ${toneClass}`}>
      <span className={`mt-0.5 ${iconClass}`} aria-hidden>{icon}</span>
      <div>
        <p className="font-semibold">{title}</p>
        <p className="mt-0.5 text-[13px] opacity-90">{children}</p>
      </div>
    </div>
  );
}

/**
 * Fenced markdown code block — Feature: rich markdown. Pulled out of
 * the NarrativeMarkdown `code` component so we can render a language
 * label + copy button alongside the syntax-highlighted code.
 *
 * <p>Unknown languages fall through to an HTML-escaped plain render
 * so a `cuneiform` fence (or a typo) still produces a readable block
 * instead of crashing the highlighter.
 */
function FencedCodeBlock({ language, children }: { language: string; children: string }) {
  const [copied, setCopied] = useState(false);
  const known = hljs.getLanguage(language) ? language : "";
  const html = known
    ? hljs.highlight(children, { language: known, ignoreIllegals: true }).value
    : escapeHtml(children);
  const label = known || "text";
  function copy() {
    navigator.clipboard.writeText(children).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      },
      () => { /* clipboard blocked — silently ignore */ },
    );
  }
  return (
    <div className="my-3 overflow-hidden rounded-md border border-datumcraft-slate/20 bg-datumcraft-fog">
      <div className="flex items-center justify-between border-b border-datumcraft-slate/20 bg-datumcraft-mist/40 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-datumcraft-slate">
        <span>{label}</span>
        <button
          type="button"
          onClick={copy}
          className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-medium normal-case tracking-normal transition-colors ${
            copied
              ? "text-emerald-700"
              : "text-datumcraft-ink hover:bg-white"
          }`}
          aria-label={copied ? "Copied" : "Copy code"}
        >
          {copied ? <CheckCircle2 size={11} aria-hidden /> : <Copy size={11} aria-hidden />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre className="overflow-x-auto p-3 text-[12px] leading-relaxed">
        <code
          className={`hljs language-${label}`}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </pre>
    </div>
  );
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function SqlBlock({ sql }: { sql: string }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const highlighted = hljs.highlight(sql, { language: "sql" }).value;

  function copy() {
    navigator.clipboard.writeText(sql).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      },
      () => { /* clipboard blocked — ignore */ },
    );
  }

  return (
    <div className="rounded-lg border border-datumcraft-slate/25 bg-datumcraft-fog">
      <div className="flex items-center justify-between px-3 py-1.5">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-datumcraft-steel hover:text-datumcraft-navy"
          aria-expanded={open}
        >
          <Code2 size={14} />
          <span>Generated SQL</span>
          <ChevronDown
            size={14}
            className={`transition-transform ${open ? "rotate-180" : ""}`}
            aria-hidden
          />
        </button>
        <button
          type="button"
          onClick={copy}
          className="inline-flex items-center gap-1 rounded border border-datumcraft-slate/30 bg-white px-2 py-0.5 text-2xs font-medium text-datumcraft-ink hover:bg-datumcraft-mist"
          title="Copy SQL to clipboard"
        >
          <Copy size={12} />
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      {open && (
        <pre
          className="sql-block border-t border-datumcraft-slate/20 rounded-none"
          dangerouslySetInnerHTML={{ __html: highlighted }}
        />
      )}
    </div>
  );
}

/**
 * Wraps a markdown {@code <table>} produced by react-markdown with a
 * sticky toolbar — row count, copy-as-TSV (clipboard-friendly), and
 * download-as-CSV. The table itself stays react-markdown's rendered
 * children; we read the cell text out of the DOM at click time so the
 * extraction stays in sync with whatever react-markdown produced
 * (links, code, formatting all flatten cleanly to text content).
 */
function ExportableMarkdownTable({ children }: { children: React.ReactNode }) {
  const tableRef = useRef<HTMLTableElement | null>(null);
  const [copied, setCopied] = useState(false);
  const [rowCount, setRowCount] = useState<number | null>(null);

  // Count rows after each render so the toolbar always shows the truth
  // even if the LLM later streamed in another batch. The narrative
  // sometimes claims "58 reports" when the table has 28; surfacing the
  // real count lets the user spot the lie at a glance.
  useEffect(() => {
    const t = tableRef.current;
    if (!t) return;
    const n = t.querySelectorAll("tbody tr").length;
    setRowCount(n);
  });

  function extract(): { columns: string[]; rows: unknown[][] } {
    const t = tableRef.current;
    if (!t) return { columns: [], rows: [] };
    const headers = Array.from(t.querySelectorAll("thead th")).map(
      (th) => (th.textContent ?? "").trim(),
    );
    const bodyRows = Array.from(t.querySelectorAll("tbody tr")).map((tr) =>
      Array.from(tr.querySelectorAll("td")).map((td) => (td.textContent ?? "").trim()),
    );
    return { columns: headers, rows: bodyRows };
  }

  function onCopy() {
    const { columns, rows } = extract();
    const tsv = [columns.join("\t"), ...rows.map((r) => r.join("\t"))].join("\n");
    navigator.clipboard.writeText(tsv).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1200);
      },
      () => {
        /* clipboard refused — best-effort, nothing to surface */
      },
    );
  }

  function onDownload() {
    const { columns, rows } = extract();
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`cloudcraftiq-operate-table-${stamp}.csv`, toCsv(columns, rows));
  }

  return (
    <div className="my-3 overflow-hidden rounded-lg border border-datumcraft-slate/25 bg-white shadow-card">
      <div className="flex items-center justify-between border-b border-datumcraft-slate/15 bg-datumcraft-fog/40 px-3 py-1.5">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-datumcraft-slate">
          <span className="inline-flex items-center gap-1.5"><Table2 size={12} aria-hidden /> Table</span>
          {rowCount !== null && (
            <span
              title="Actual rows in this table — narrative numbers should match this. If they don't, the answer is hallucinated."
              className="rounded-full bg-white px-2 py-0.5 normal-case font-medium text-datumcraft-ink"
            >
              {rowCount.toLocaleString()} {rowCount === 1 ? "row" : "rows"}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onCopy}
            title="Copy as TSV (paste into Excel, Sheets, …)"
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-datumcraft-slate hover:bg-white hover:text-datumcraft-navy"
          >
            <Copy size={11} aria-hidden /> {copied ? "Copied" : "Copy"}
          </button>
          <button
            type="button"
            onClick={onDownload}
            title="Download as CSV"
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-datumcraft-slate hover:bg-white hover:text-datumcraft-navy"
          >
            <Download size={11} aria-hidden /> CSV
          </button>
        </div>
      </div>
      <div className="max-h-[420px] overflow-auto">
        <table ref={tableRef} className="min-w-full border-collapse text-sm">
          {children}
        </table>
      </div>
    </div>
  );
}

/**
 * Turns an MCP tool id (e.g. {@code prod:searchContent},
 * {@code cognosBrowseContent}) into a Title-Case label fit for a
 * table header — drops the namespace, splits camelCase, capitalises
 * the first word. Returns {@code undefined} when input is missing so
 * the caller's prop default kicks in.
 */
function humanizeToolName(tool?: string): string | undefined {
  if (!tool) return undefined;
  const stripped = tool.includes(":") ? tool.split(":").pop()! : tool;
  // Split on camelCase, snake_case, kebab-case, dots — flatten to words.
  const words = stripped
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[._-]+/g, " ")
    .toLowerCase()
    .trim();
  if (!words) return undefined;
  return words.charAt(0).toUpperCase() + words.slice(1);
}
